import { useState, KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useGetTagsQuery } from '@/redux/tag/tag.api.slice';
import { ITagType } from '@/redux/tag/tag.d';

interface TagInputProps {
  tags: string[];
  onAdd: (tag: string) => void;
  onRemove: (index: number) => void;
  invalid?: boolean;
}

const MAX_SUGGESTIONS = 8;

type Tag = { _id?: string; name: string };

/** Backend tags matching the current draft, minus the ones already added, and
 * only while the draft is non-empty and the box is focused. */
function computeSuggestions(
  allTags: Tag[] | undefined,
  trimmedDraft: string,
  tags: string[],
  show: boolean
): Tag[] {
  if (!show || !trimmedDraft) return [];
  return (allTags ?? [])
    .filter(
      (tag) =>
        tag.name.toLowerCase().includes(trimmedDraft) &&
        !tags.includes(tag.name)
    )
    .slice(0, MAX_SUGGESTIONS);
}

export default function TagInput({
  tags,
  onAdd,
  onRemove,
  invalid,
}: TagInputProps) {
  const [draft, setDraft] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { data: allTags } = useGetTagsQuery({ type: ITagType.POST });

  const trimmedDraft = draft.trim().toLowerCase();
  const suggestions = computeSuggestions(
    allTags,
    trimmedDraft,
    tags,
    showSuggestions
  );

  const handleAdd = (tag: string) => {
    onAdd(tag);
    setDraft('');
    setShowSuggestions(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const t = draft.trim();
      if (t) {
        handleAdd(t);
      }
    }
  };

  return (
    <div className="relative">
      <div
        className={cn(
          'flex flex-wrap gap-2 items-center bg-muted/30 border rounded-lg px-3 py-2 focus-within:border-primary focus-within:ring-1 focus-within:ring-primary',
          invalid && 'border-destructive focus-within:border-destructive'
        )}
      >
        {/* Tinted rather than marked: a tag is whatever the author typed, so
            there is no icon that could stand for one honestly. */}
        {tags.map((tag, i) => (
          <Badge
            key={tag}
            variant="secondary"
            className="cursor-pointer gap-1 border-primary/20 bg-primary/10 text-xs font-medium text-primary hover:bg-primary/15"
          >
            {tag}
            <button
              type="button"
              onClick={() => onRemove(i)}
              aria-label={`Remove ${tag}`}
              className="-m-1.5 ml-0.5 p-1.5 text-primary/60 hover:text-destructive"
            >
              ×
            </button>
          </Badge>
        ))}
        <Input
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setShowSuggestions(true);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setShowSuggestions(false)}
          placeholder="type a tag, press Enter"
          className="border-none shadow-none bg-transparent flex-1 min-w-[90px] h-7 p-0 text-sm focus-visible:ring-0"
        />
      </div>
      {suggestions.length > 0 && (
        <div className="absolute z-10 mt-1 w-full max-h-48 overflow-auto rounded-lg border bg-popover text-popover-foreground shadow-md">
          {suggestions.map((tag) => (
            <button
              key={tag._id ?? tag.name}
              type="button"
              onMouseDown={(e) => {
                // fires before the input's onBlur so the click isn't lost
                e.preventDefault();
                handleAdd(tag.name);
              }}
              className="block w-full px-3 py-1.5 text-left text-sm hover:bg-muted"
            >
              {tag.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
